/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import Messenger from "App/messenger";
import {DataResource} from "App/storage/data_resource";
import Renderer from "App/renderer";
import IScene from "js_interface/src/scene_interface";
import RenderObject from "App/geometry/objects/object";

export default
class RenderGraphLoadStatus {

    private _sceneAPI: IScene;
    private _renderer: Renderer;
    private allBytes: {
        required: number,
        done: number
    };

    private sceneLoadedEventAlreadyDispatched: boolean = false;
    private bytesPerModel: {} = {};
    private downloadFailedOccured: boolean = false;
    private loadProgressCB: () => void;
    private loadProgressInterval: number = 0;

    constructor(aSceneAPI: IScene, aRenderer: Renderer) {
        this._sceneAPI = aSceneAPI;
        this._renderer = aRenderer;

        this.allBytes = {
            required: 0,
            done: 0
        };

        this.loadProgressCB = (): void => {
            if (this.allBytes.required !== 0) {
                Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_sceneLoadStatusChanged", {
                    progress: ((this.allBytes.done) / this.allBytes.required) * 100
                });
            }
            else { // all data is downloaded, but we need to wait untill all data at the lowest detail are also rendered
                let document = this._renderer.getDocument();
                let mainModel = document.getMainModel();
                let gos: RenderObject[] = mainModel.getGeometryObjects();
                for (let i = 0; i < gos.length; i++) {
                    if (!gos[i].getIsReadyToRender()) {
                        return;
                    }
                }

                if (this.loadProgressInterval) {
                    clearInterval(this.loadProgressInterval);
                    this.loadProgressInterval = null;
                }

                if (!this.sceneLoadedEventAlreadyDispatched) {
                    Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_sceneLoaded", {});
                    this.sceneLoadedEventAlreadyDispatched = true;
                    this._sceneAPI.setSceneLoaded(true);
                }
            }
        };
        this.loadProgressInterval = null;
    }


    /**
     * If download failed occurred then the "cwx3d_sceneLoaded" event is controlled by the
     * the case when the next download query has different priority than 0 (the highest)
     * or in case that there is no query to download
     */
    public downloadFailed(aRequest: {}): void {
    }

    public downloadAborted(aRequest: {}): void {
    }

    public requestBytes(aResource: DataResource): void {
        if (aResource.isRequired()) {
            this.allBytes.required += aResource.getBinarySize();

            let modelBytes = this.bytesPerModel[aResource.getSAPIModelUID()];
            if (modelBytes === undefined) { // create a new record for new model
                modelBytes = {
                    required: 0,
                    done: 0,
                    modelLoadedEventAlreadyDispatched: false
                };
                this.bytesPerModel[aResource.getSAPIModelUID()] = modelBytes;
            }

            modelBytes.required += aResource.getBinarySize();
        }
    }

    public releaseBytes(aResource: DataResource, aBytesCount: number) {
        if (aResource.isRequired()) {
            this.allBytes.required -= aBytesCount;

            let modelBytes = this.bytesPerModel[aResource.getSAPIModelUID()];
            modelBytes.required -= aBytesCount;

            this.checkModelOrSceneLoaded();
        }
    }

    public receiveBytes(aResource: DataResource, aBytesCount: number) {
        if (aResource.isRequired()) {
            this.allBytes.done += aBytesCount;

            let modelBytes = this.bytesPerModel[aResource.getSAPIModelUID()];
            modelBytes.done += aBytesCount;

            this.checkModelOrSceneLoaded();
        }
    }

    private checkModelOrSceneLoaded(): void {
        // instead of updating the progress bar when the load state of the objects changed, schedule a timer once by second to update the load progress
        if (!this.loadProgressInterval) {
            this.loadProgressInterval = window.setInterval(this.loadProgressCB, 250);
        }

        // pass through particular models stats and create and model loaded event when all data are downloaded for particular models
        for (let key in this.bytesPerModel) {
            let bytes = this.bytesPerModel[key];
            if ((bytes.required - bytes.done) === 0) {
                if (!bytes.modelLoadedEventAlreadyDispatched) {
                    Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_modelLoaded", {
                        SAPIModelUID: parseInt(key)
                    });
                    bytes.modelLoadedEventAlreadyDispatched = true;
                }
            }
        }

        // check if all models are downloaded
        if ((this.allBytes.required - this.allBytes.done) === 0) {
            /*if (this.loadProgressInterval) {
                clearInterval(this.loadProgressInterval);
                this.loadProgressInterval = null;
            }*/

            /*if (!this.sceneLoadedEventAlreadyDispatched) {
                Messenger.fireCDWKEvent(this._sceneAPI.getTargetTag(), "cwx3d_sceneLoaded", {});
                this.sceneLoadedEventAlreadyDispatched = true;
                this._sceneAPI.setSceneLoaded(true);
            }*/

            this.allBytes.required -= this.allBytes.done;
            this.allBytes.done = 0;
        }
    }
}
